import React, { useMemo } from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import StackedTrendChart from "../charts/StackedTrendChart";
import { humanEUR, pct } from "./format";

// latestActualSplit - the Cloud/AI euro split (+ each's share of the month total) for the
// most recent *actual* (non-forecast) month, so the headline split stays anchored to
// settled spend rather than a projection. Returns null when there is no actual month at
// all (brand-new customer with only forward-looking forecast points, or no data yet) -
// the block simply omits the split rather than mislabeling a forecast as "current".
export function latestActualSplit(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  const actuals = points.filter((p) => p && !p.is_forecast);
  if (actuals.length === 0) return null;
  const point = actuals[actuals.length - 1];
  const cloud = point.cloud ?? 0;
  const ai = point.ai ?? 0;
  const total = cloud + ai;
  return {
    month: point.month,
    cloud,
    ai,
    cloudShare: total ? cloud / total : null,
    aiShare: total ? ai / total : null,
  };
}

/**
 * StackedTrendBlock - Cloud+AI stacked spend trend vs. budget (stacked_trend block type),
 * plus the current Cloud/AI split (euro + share of total) called out beside the chart.
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/trend -> envelope key
 * "trend" -> boardfigures.TrendSeries { points: [{month, cloud, ai, budget, is_forecast}] }.
 */
const StackedTrendBlock = ({ title = "Spend trend", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/trend`
  );

  const points = useMemo(() => data?.trend?.points ?? [], [data]);
  const split = useMemo(() => latestActualSplit(points), [points]);

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-stacked-trend">
      <StackedTrendChart points={points} />
      {split && (
        <div className="a4-trend-split" aria-label="Current Cloud/AI split">
          <div className="a4-trend-split-item">
            <div className="a4-trend-split-label">Cloud</div>
            <div className="a4-trend-split-value">{humanEUR(split.cloud)}</div>
            {split.cloudShare != null && (
              <div className="a4-trend-split-pct">{pct(split.cloudShare)}</div>
            )}
          </div>
          <div className="a4-trend-split-item">
            <div className="a4-trend-split-label">AI</div>
            <div className="a4-trend-split-value">{humanEUR(split.ai)}</div>
            {split.aiShare != null && (
              <div className="a4-trend-split-pct">{pct(split.aiShare)}</div>
            )}
          </div>
        </div>
      )}
    </BaseBlock>
  );
};

StackedTrendBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default StackedTrendBlock;
